import React, { useState, useEffect, useContext } from 'react';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  CircularProgress,
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import AuthContext from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';

export default function Patients() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);
  const { selectedPractice } = usePractice();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Dialog state for create/edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    sex: 'unknown',
    dob: '',
    mrn: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: ''
  });
  // Validation helper for YYYY-MM-DD
  const isValidDob = (str) => /^\d{4}-\d{2}-\d{2}$/.test(str);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('last_name');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedPatients = patients.slice().sort((a, b) => {
    let aVal = a[orderBy] ?? '';
    let bVal = b[orderBy] ?? '';
    if (typeof aVal === 'string') {
      const cmp = aVal.localeCompare(bVal);
      return order === 'asc' ? cmp : -cmp;
    }
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });

  // Load patient records for the selected practice
  const fetchPatients = async () => {
    if (!tokens?.id_token || !selectedPractice?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/patients?organization_id=${selectedPractice.id}`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch patients');
      const data = await res.json();
      setPatients(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchPatients();
  }, [tokens, selectedPractice]);

  if (loading) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }
  if (error) {
    return (
      <Container disableGutters>
        <Typography color="error" variant="h6" sx={{ mt: 4 }}>
          Error: {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Patients
        </Typography>
        <Button variant="contained" onClick={() => {
          setEditingPatient(null);
          setFormData({
            first_name: '',
            middle_name: '',
            last_name: '',
            sex: 'unknown',
            dob: '',
            mrn: '',
            email: '',
            phone: '',
            address_1: '',
            address_2: '',
            city: '',
            state: '',
            zip_code: ''
          });
          setDialogOpen(true);
        }}>
          Add Patient
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'id' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleRequestSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'first_name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'first_name'}
                  direction={orderBy === 'first_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('first_name')}
                >
                  First Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'last_name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'last_name'}
                  direction={orderBy === 'last_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('last_name')}
                >
                  Last Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'dob' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'dob'}
                  direction={orderBy === 'dob' ? order : 'asc'}
                  onClick={() => handleRequestSort('dob')}
                >
                  DOB
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'mrn' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'mrn'}
                  direction={orderBy === 'mrn' ? order : 'asc'}
                  onClick={() => handleRequestSort('mrn')}
                >
                  MRN
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPatients.map((p) => {
              const shortId = String(p.id).split('-')[0];
              return (
                <TableRow key={p.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Tooltip title={p.id} arrow>
                        <Typography variant="body2" sx={{ cursor: 'default' }}>
                          {shortId}
                        </Typography>
                      </Tooltip>
                      <Tooltip title="Copy full ID" arrow>
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard.writeText(p.id)}
                          sx={{ ml: 0.5 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{p.first_name}</TableCell>
                  <TableCell>{p.last_name}</TableCell>
                  <TableCell>{p.dob}</TableCell>
                  <TableCell>{p.mrn}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => {
                      setEditingPatient(p);
                      setFormData({
                        first_name: p.first_name,
                        middle_name: p.middle_name || '',
                        last_name: p.last_name,
                        sex: p.sex || 'unknown',
                        dob: p.dob,
                        mrn: p.mrn,
                        email: p.email || '',
                        phone: p.phone || '',
                        address_1: p.address_1 || '',
                        address_2: p.address_2 || '',
                        city: p.city || '',
                        state: p.state || '',
                        zip_code: p.zip_code || ''
                      });
                      setDialogOpen(true);
                    }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editingPatient ? 'Edit Patient' : 'Add Patient'}</DialogTitle>
      <DialogContent>
        <Typography variant="h6" gutterBottom>Demographics</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <TextField
            required
            fullWidth
            label="First Name"
            name="first_name"
            value={formData.first_name}
            onChange={e => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
          />
          <TextField
            required
            fullWidth
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            onChange={e => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Middle Name"
            name="middle_name"
            value={formData.middle_name}
            onChange={e => setFormData(prev => ({ ...prev, middle_name: e.target.value }))}
          />
          <TextField
            fullWidth
            select
            label="Sex"
            name="sex"
            value={formData.sex}
            onChange={e => setFormData(prev => ({ ...prev, sex: e.target.value }))}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="unknown">Unknown</MenuItem>
          </TextField>
          <TextField
            required
            fullWidth
            label="DOB (YYYY-MM-DD)"
            name="dob"
            value={formData.dob}
            onChange={e => setFormData(prev => ({ ...prev, dob: e.target.value }))}
            error={!!formData.dob && !isValidDob(formData.dob)}
            helperText={!!formData.dob && !isValidDob(formData.dob) ? 'Invalid format' : ''}
          />
          <TextField
            required
            fullWidth
            label="MRN"
            name="mrn"
            value={formData.mrn}
            onChange={e => setFormData(prev => ({ ...prev, mrn: e.target.value }))}
          />
        </Box>
        <Typography variant="h6" gutterBottom>Address</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <TextField
            required
            fullWidth
            label="Address 1"
            name="address_1"
            value={formData.address_1}
            onChange={e => setFormData(prev => ({ ...prev, address_1: e.target.value }))}
          />
          <TextField fullWidth label="Address 2" name="address_2" value={formData.address_2} onChange={e => setFormData(prev => ({ ...prev, address_2: e.target.value }))} />
          <TextField
            required
            fullWidth
            label="City"
            name="city"
            value={formData.city}
            onChange={e => setFormData(prev => ({ ...prev, city: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              required
              fullWidth
              label="State"
              name="state"
              value={formData.state}
              onChange={e => setFormData(prev => ({ ...prev, state: e.target.value }))}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <TextField
              required
              fullWidth
              label="Zip Code"
              name="zip_code"
              value={formData.zip_code}
              onChange={e => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>
        </Box>
        <Typography variant="h6" gutterBottom>Contact</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Email" name="email" value={formData.email} onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Phone" name="phone" value={formData.phone} onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))} />
          </Grid>
        </Grid>
      </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              loading ||
              !formData.first_name ||
              !formData.last_name ||
              !isValidDob(formData.dob) ||
              !formData.mrn ||
              !formData.address_1 ||
              !formData.city ||
              !formData.state ||
              !formData.zip_code
            }
            onClick={async () => {
              setLoading(true);
              try {
                const method = editingPatient ? 'PUT' : 'POST';
                const url = editingPatient
                  ? `${apiBase}/patients/${editingPatient.id}`
                  : `${apiBase}/patients`;
                const res = await fetch(url, {
                  method,
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${tokens.id_token}`
                  },
                  body: JSON.stringify({ ...formData, organization_id: selectedPractice?.id })
                });
                if (!res.ok) throw new Error(await res.text() || 'Failed to save patient');
                await fetchPatients();
                setDialogOpen(false);
              } catch (err) {
                console.error(err);
                setError(err.message);
              } finally {
                setLoading(false);
              }
            }}
          >{loading ? <CircularProgress size={24} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
